/**
 * Hook chamado UMA vez pelo app-core (`ui/modules.tsx`): o índice, a camada,
 * a limpeza e o ocultador das camadas observadas (com a foto ao fundo) e o
 * modo exclusivo das fichas por toque.
 */
import { useEffect } from 'react';
import { useMapInstance } from '../map/mapContext';
import { CONSUMIDO, registerCoordinatePopup, registerModoExclusivo } from '../map/popupControl';
import { mapToLonLat } from '../map/projection';
import { BASE_PREVISAO } from './api';
import { iniciarLimpeza } from './limpar';
import { iniciarIndice, PrevisaoLayerManager } from './motor';
import { iniciarOcultador } from './ocultar';
import { posicao } from './ponto';
import { emModoPrevisao, usePrevisaoStore, tempoEmUso } from './store';

export function usePrevisaoLayers(): void {
  const map = useMapInstance((s) => s.map);

  useEffect(() => (BASE_PREVISAO ? iniciarIndice() : undefined), []);
  useEffect(() => (BASE_PREVISAO ? iniciarLimpeza() : undefined), []);
  useEffect(() => registerModoExclusivo(emModoPrevisao), []);
  // No modo, o toque no vazio abre a ficha do ponto (as camadas observadas estão ocultas) — no
  // cartão da direita ou na folha do celular, não num balão: o clique é consumido.
  useEffect(
    () =>
      registerCoordinatePopup(
        ([x, y]) => {
          const s = usePrevisaoStore.getState();
          // A área do modelo do tempo em uso (o ICON, em 0,125°, acaba meia célula antes dos outros).
          const g = s.indice?.modelos[tempoEmUso(s)]?.grade;
          if (!s.modo || !g) return null;
          const [lon, lat] = mapToLonLat(x, y);
          if (!posicao(g, lon, lat)) return null;
          s.abrirPonto(lon, lat);
          return CONSUMIDO;
        },
        { prioridade: 10, noModoExclusivo: true },
      ),
    [],
  );
  // Link com `l`: a ficha abre quando o mapa, o modo e o índice estão prontos.
  useEffect(() => {
    if (!map) return;
    const tentar = () => {
      const s = usePrevisaoStore.getState();
      if (!s.pontoDoLink || !s.indice) return;
      const p = s.pontoDoLink;
      usePrevisaoStore.setState({ pontoDoLink: null });
      // Link com `l` sem o modo (sem `y`/`d`, ou a exportação travou a entrada): o ponto é esquecido.
      if (!s.modo) return;
      s.abrirPonto(p[0], p[1]);
    };
    tentar();
    return usePrevisaoStore.subscribe(tentar);
  }, [map]);

  useEffect(() => {
    if (!map || !BASE_PREVISAO) return;
    const manager = new PrevisaoLayerManager(map);
    const pararOcultador = iniciarOcultador(map);
    return () => {
      pararOcultador();
      manager.dispose();
    };
  }, [map]);
}
